import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useAppMetadata } from '../../contexts/AppMetadataContext';
import { useI18n } from '../../locales/useI18n';
import { breakpoints, borderRadius, spacing, typography } from '../../styles/theme';
import { getUpcomingEventCounts, isAppMetadataFresh } from '../../utils/appMetadata';

const pulse = keyframes`
  0%, 100% { opacity: 0.35; }
  50% { opacity: 0.75; }
`;

const ActivityRail = styled.section`
  width: 100%;
  max-width: 1040px;
  min-height: 167px;
  margin: 0 auto ${spacing[6]};
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: ${borderRadius.lg};
  background: rgba(0, 21, 41, 0.34);
  box-shadow: 0 12px 32px rgba(0, 21, 41, 0.2);
  backdrop-filter: blur(8px);

  @media (max-width: ${breakpoints.sm}) {
    min-height: 185px;
    margin-bottom: ${spacing[5]};
  }
`;

const PrimaryStats = styled.dl.withConfig({
  shouldForwardProp: (prop) => prop !== '$columns',
})<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$columns}, minmax(0, 1fr));
  gap: 1px;
  margin: 0;
  background: rgba(255, 255, 255, 0.18);

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const PrimaryStat = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  padding: ${spacing[3]} ${spacing[4]};
  background: rgba(0, 21, 41, 0.45);
  text-align: left;

  @media (max-width: ${breakpoints.sm}) {
    padding: ${spacing[2]} 6px;
  }
`;

const StatValue = styled.dd`
  order: 1;
  margin: 0;
  color: #ffffff;
  font-size: ${typography.fontSizes['2xl']};
  font-weight: ${typography.fontWeights.bold};
  line-height: 1.15;
  font-variant-numeric: tabular-nums;

  @media (max-width: ${breakpoints.sm}) {
    font-size: ${typography.fontSizes.lg};
  }
`;

const StatLabel = styled.dt`
  order: 2;
  margin-top: ${spacing[1]};
  color: rgba(255, 255, 255, 0.78);
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};
  line-height: 1.25;
  letter-spacing: 0;

  @media (max-width: ${breakpoints.sm}) {
    font-size: 10px;
  }
`;

const CoverageRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${spacing[4]};
  padding: ${spacing[3]} ${spacing[4]};
  border-top: 1px solid rgba(255, 255, 255, 0.14);
  color: rgba(255, 255, 255, 0.88);
  text-align: left;

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${spacing[2]};
  }

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${spacing[2]};
    padding: ${spacing[2]} ${spacing[3]};
  }
`;

const CoverageFact = styled.div`
  min-width: 0;
  font-size: ${typography.fontSizes.sm};
  line-height: 1.35;

  strong {
    color: #ffffff;
    font-weight: ${typography.fontWeights.semibold};
    font-variant-numeric: tabular-nums;
  }

  small {
    display: block;
    color: rgba(255, 255, 255, 0.65);
    font-size: ${typography.fontSizes.xs};
  }

  @media (max-width: ${breakpoints.sm}) {
    font-size: ${typography.fontSizes.xs};

    &:nth-child(3) {
      grid-column: 1 / -1;
      text-align: center;
    }

    small {
      font-size: 10px;
    }
  }
`;

const FreshnessRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]} ${spacing[4]};
  padding: ${spacing[2]} ${spacing[4]};
  border-top: 1px solid rgba(255, 255, 255, 0.14);
  color: rgba(255, 255, 255, 0.72);
  font-size: ${typography.fontSizes.xs};
  line-height: 1.4;

  @media (max-width: ${breakpoints.sm}) {
    gap: ${spacing[1]} ${spacing[2]};
    padding: 6px ${spacing[2]};
    font-size: 10px;
  }
`;

const FreshDot = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== '$fresh',
})<{ $fresh: boolean }>`
  display: inline-block;
  width: 7px;
  height: 7px;
  flex: 0 0 7px;
  border-radius: 50%;
  background: ${(props) => (props.$fresh ? '#73d13d' : 'rgba(255, 255, 255, 0.42)')};
  box-shadow: ${(props) => (props.$fresh ? '0 0 0 3px rgba(115, 209, 61, 0.16)' : 'none')};
  vertical-align: middle;
`;

const LoadingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1px;
  height: 84px;
  background: rgba(255, 255, 255, 0.16);

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    height: 183px;
  }
`;

const LoadingCell = styled.span`
  background: rgba(255, 255, 255, 0.08);
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

export const HomeActivityStats: React.FC = () => {
  const { metadata, isLoading } = useAppMetadata();
  const { locale, t } = useI18n();

  if (!metadata) {
    if (!isLoading) return null;
    return (
      <ActivityRail aria-busy="true" aria-label={t('homepage.liveStats.loading')}>
        <LoadingGrid aria-hidden="true">
          {[0, 1, 2, 3].map((key) => <LoadingCell key={key} />)}
        </LoadingGrid>
      </ActivityRail>
    );
  }

  const number = new Intl.NumberFormat(locale);
  const counts = getUpcomingEventCounts(metadata);
  const primary = [
    { label: t('homepage.liveStats.upcoming'), value: counts.total },
    { label: t('homepage.liveStats.inPerson'), value: counts.inPerson },
    { label: t('homepage.liveStats.virtual'), value: counts.virtual },
    { label: t('homepage.liveStats.hybrid'), value: counts.hybrid },
  ];
  const coverage = metadata.upcomingCoverage;
  const refreshedAt = new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(metadata.builtAt));
  const fresh = isAppMetadataFresh(metadata.builtAt);

  return (
    <ActivityRail aria-label={t('homepage.liveStats.ariaLabel')}>
      <PrimaryStats $columns={4}>
        {primary.map((item) => (
          <PrimaryStat key={item.label}>
            <StatLabel>{item.label}</StatLabel>
            <StatValue>{number.format(item.value)}</StatValue>
          </PrimaryStat>
        ))}
      </PrimaryStats>

      {coverage && (
        <CoverageRow>
          <CoverageFact>
            <strong>{number.format(coverage.organizations)}</strong>{' '}
            {t('homepage.liveStats.organizations')}
            <small>
              {number.format(coverage.partners)} {t('homepage.liveStats.partners')}
              {' + '}
              {number.format(coverage.companies)} {t('homepage.liveStats.companies')}
            </small>
          </CoverageFact>
          <CoverageFact>
            <strong>{number.format(coverage.websiteSources)}</strong>{' '}
            {t('homepage.liveStats.webSources')}
          </CoverageFact>
          <CoverageFact>
            <strong>{number.format(coverage.cities)}</strong> {t('homepage.liveStats.cities')}
            {' | '}
            <strong>{number.format(coverage.countries)}</strong> {t('homepage.liveStats.countries')}
            {' | '}
            <strong>{number.format(coverage.continents)}</strong> {t('homepage.liveStats.continents')}
          </CoverageFact>
        </CoverageRow>
      )}

      <FreshnessRow>
        <span>
          <strong>{number.format(metadata.eventsAddedPastWeek)}</strong>{' '}
          {t('homepage.liveStats.addedPastWeek')}
        </span>
        <span aria-hidden="true">|</span>
        <span>
          <FreshDot $fresh={fresh} aria-hidden="true" />{' '}
          {fresh && `${t('homepage.liveStats.fresh')} | `}
          {t('homepage.liveStats.updated')} {refreshedAt}
        </span>
      </FreshnessRow>
    </ActivityRail>
  );
};

export default HomeActivityStats;